import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, XCircle, MessageCircle, Trash2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestBookManager({ authHeader }) {
  const [entries, setEntries] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/guestbook`, authHeader);
      setEntries(res.data.entries);
    } catch (e) {
      console.error(e);
    }
  }, [authHeader]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/guestbook/${id}/status`, { status }, authHeader);
    fetchEntries();
  };

  const submitReply = async (id) => {
    if (!replyText.trim()) return;
    await axios.put(`${API}/admin/guestbook/${id}/reply`, { reply: replyText }, authHeader);
    setReplyingTo(null);
    setReplyText("");
    fetchEntries();
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await axios.delete(`${API}/admin/guestbook/${id}`, authHeader);
    fetchEntries();
  };

  const statusColors = {
    approved: "bg-green-100 text-green-700",
    pending: "bg-yellow-100 text-yellow-700",
    rejected: "bg-red-100 text-red-700",
  };

  return (
    <div data-testid="guestbook-manager">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-display text-wc-forest text-lg font-semibold mb-4">
          Guest Book Entries ({entries.length})
        </h3>
        {entries.length === 0 ? (
          <p className="font-body text-gray-400 text-sm">No entries yet.</p>
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => (
              <div key={entry.id} className="border border-gray-100 rounded-md p-4" data-testid={`gb-entry-${entry.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-display text-wc-forest font-semibold text-sm">{entry.name}</span>
                      {entry.visit_date && (
                        <span className="font-body text-gray-400 text-xs">{entry.visit_date}</span>
                      )}
                      <span className={`px-2 py-0.5 rounded text-xs font-body ${statusColors[entry.status] || statusColors.pending}`}>
                        {entry.status || "pending"}
                      </span>
                    </div>
                    <p className="font-body text-gray-600 text-sm">{entry.message}</p>
                    {entry.reply && (
                      <div className="mt-2 pl-4 border-l-2 border-wc-teal/30">
                        <p className="font-body text-wc-teal text-sm italic">Your reply: {entry.reply}</p>
                      </div>
                    )}
                    <p className="font-body text-gray-300 text-xs mt-2">{new Date(entry.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {entry.status !== "approved" && (
                      <Button size="sm" variant="ghost" onClick={() => updateStatus(entry.id, "approved")}
                        className="text-green-600 hover:bg-green-50 h-8 w-8 p-0" title="Approve">
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                    {entry.status !== "rejected" && (
                      <Button size="sm" variant="ghost" onClick={() => updateStatus(entry.id, "rejected")}
                        className="text-red-500 hover:bg-red-50 h-8 w-8 p-0" title="Reject">
                        <XCircle className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost"
                      onClick={() => { setReplyingTo(replyingTo === entry.id ? null : entry.id); setReplyText(entry.reply || ""); }}
                      className="text-wc-teal hover:bg-wc-teal/10 h-8 w-8 p-0" title="Reply">
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteEntry(entry.id)}
                      className="text-gray-400 hover:bg-gray-100 h-8 w-8 p-0" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {replyingTo === entry.id && (
                  <div className="mt-3 flex gap-2">
                    <Input
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Write a reply..."
                      className="flex-1 text-sm"
                      data-testid="gb-reply-input"
                    />
                    <Button size="sm" onClick={() => submitReply(entry.id)}
                      className="bg-wc-teal text-white hover:bg-wc-forest text-xs"
                      data-testid="gb-reply-submit"
                    >
                      Send Reply
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
